import { useState, useMemo } from 'react'
import { useLeadSourceAnalysis } from '@/hooks/use-api'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { ReportFilters, getDateRange as getReportDateRange } from '@/components/ReportFilters'
import type { ReportFilterValues } from '@/components/ReportFilters'
import {
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts'
import { Download, TrendingUp, Users, Target, DollarSign, Loader2 } from 'lucide-react'
import { formatCurrency, formatPercent, formatDate, toDisplayDate } from '@/lib/utils'
import {
  CHART_COLORS,
  DEFAULT_CHART_MARGIN,
  formatChartCount,
  CATEGORICAL,
  SEMANTIC,
} from '@/lib/theme'
import { exportToCSV } from '@/utils/csvExport'
import { ChartTooltipContent } from '@/components/ui/chart-tooltip'
import { explainError } from '@/lib/explain-error'

const COLORS = CHART_COLORS

export default function LeadSourceAnalysis() {
  const [filters, setFilters] = useState<ReportFilterValues>(() =>
    getReportDateRange('last_12_months')
  )

  const { data, isLoading, error } = useLeadSourceAnalysis({
    startDate: filters.startDate,
    endDate: filters.endDate,
    owner_id: filters.owner_id,
  })

  // Transform distribution data for pie chart
  const distributionChartData = useMemo(() => {
    if (!data?.distribution) return []
    return data.distribution.map((item: any) => ({
      name: item.lead_source,
      value: item.total_leads,
      conversionRate: parseFloat(item.conversion_rate || '0'),
    }))
  }, [data])

  // Total leads across all sources (used in donut center stat + tooltip)
  const totalLeads = useMemo(
    () => distributionChartData.reduce((sum: number, d: any) => sum + (d.value || 0), 0),
    [distributionChartData]
  )

  // Legend rows: largest → smallest. Carry the original index (`colorIndex`)
  // so each row's swatch matches its donut slice color, since Recharts
  // assigns colors by the unsorted data array order.
  const sortedDistribution = useMemo(
    () =>
      distributionChartData
        .map((item: any, index: number) => ({ ...item, colorIndex: index }))
        .sort((a: any, b: any) => b.value - a.value),
    [distributionChartData]
  )

  // Transform trends data for line chart
  const trendsChartData = useMemo(() => {
    if (!data?.trends) return []

    // Group by month
    const monthGroups = data.trends.reduce((acc: any, item: any) => {
      const monthKey = (toDisplayDate(item.month) ?? new Date(item.month)).toLocaleDateString(
        'en-US',
        {
          month: 'short',
          year: 'numeric',
        }
      )
      if (!acc[monthKey]) {
        acc[monthKey] = { month: monthKey }
      }
      acc[monthKey][item.lead_source] = item.lead_count
      return acc
    }, {})

    return Object.values(monthGroups)
  }, [data])

  // Get unique lead sources for line chart
  const uniqueSources = useMemo(() => {
    if (!data?.trends) return []
    return [...new Set(data.trends.map((item: any) => item.lead_source))]
  }, [data])

  // Transform top performers data for bar chart
  const topPerformersChartData = useMemo(() => {
    if (!data?.topPerformers) return []
    return data.topPerformers.map((item: any) => ({
      source: item.lead_source,
      conversionRate: parseFloat(item.conversion_rate || '0'),
      totalLeads: item.total_leads,
      convertedLeads: item.converted_leads,
      avgDaysToConvert: parseFloat(item.avg_days_to_convert || '0'),
    }))
  }, [data])

  const summary = data?.summary

  const handleExportCSV = () => {
    if (!data?.distribution) return

    const exportData = data.distribution.map((item: any) => ({
      'Lead Source': item.lead_source,
      'Total Leads': item.total_leads,
      'Qualified Leads': item.qualified_leads,
      'Converted Leads': item.converted_leads,
      'Conversion Rate': `${item.conversion_rate}%`,
      'Total Revenue': formatCurrency(item.total_revenue),
      'Avg Deal Size': formatCurrency(item.avg_deal_size),
    }))

    exportToCSV({ data: exportData, filename: 'lead-source-analysis.csv' })
  }

  // Check if we have data but it's empty (no results for date range)
  const hasEmptyResults =
    !isLoading && !error && data && (!data.distribution || data.distribution.length === 0)

  if (error) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Lead Source Analysis</h1>
          <p className="text-muted-foreground">
            Analyze lead sources, conversion rates, and performance metrics
          </p>
        </div>
        <Card>
          <CardContent className="pt-6">
            <div className="space-y-4">
              <p className="text-destructive font-medium">
                Failed to load lead source analysis data.
              </p>
              <p className="text-sm text-muted-foreground">
                Error: {explainError(error, 'Unknown error occurred')}
              </p>
              <Button
                onClick={() => window.location.reload()}
                variant="outline"
                aria-label="Reload page"
              >
                Try Again
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Lead Source Analysis</h1>
          <p className="text-muted-foreground">
            Analyze lead sources, conversion rates, and performance metrics
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <ReportFilters
            value={filters}
            onChange={setFilters}
            defaultPeriod="last_12_months"
            datePresets={[
              'this_month',
              'this_quarter',
              'fiscal_year',
              'calendar_year',
              'last_12_months',
              'all',
              'custom',
            ]}
          />
          <Button
            onClick={handleExportCSV}
            variant="outline"
            disabled={isLoading || !data?.distribution}
            aria-label="Export lead source analysis to CSV"
            className="self-end"
          >
            <Download className="mr-2 h-4 w-4" aria-hidden="true" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Empty State Warning */}
      {hasEmptyResults && (
        <Card
          style={{
            backgroundColor: SEMANTIC.warning.fill,
            borderColor: SEMANTIC.warning.fill,
          }}
        >
          <CardContent className="pt-6">
            <div className="space-y-2">
              <p className="font-medium" style={{ color: SEMANTIC.warning.text }}>
                No leads found in the selected date range
              </p>
              <p className="text-sm" style={{ color: SEMANTIC.warning.text }}>
                {filters.startDate && filters.endDate ? (
                  <>
                    Your filter from {formatDate(filters.startDate)} to{' '}
                    {formatDate(filters.endDate)} returned no results. Try adjusting your date range
                    or clearing the filters to see all data.
                  </>
                ) : (
                  'No lead data available. Try clearing the filters or selecting a different date range.'
                )}
              </p>
              {(filters.startDate || filters.endDate) && (
                <Button
                  onClick={() => setFilters(getReportDateRange('last_12_months'))}
                  variant="outline"
                  size="sm"
                  className="mt-2"
                  aria-label="Clear date range filters"
                >
                  Clear Filters
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Summary Statistics */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2
            className="h-8 w-8 animate-spin text-muted-foreground"
            aria-label="Loading lead source analysis data"
          />
        </div>
      ) : (
        <>
          <div className="grid gap-4 md:grid-cols-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-0">
                <CardTitle className="text-sm font-medium">Total Leads</CardTitle>
                <Users
                  className="h-4 w-4"
                  aria-hidden="true"
                  style={{ color: CATEGORICAL.prussian.solid }}
                />
              </CardHeader>
              <CardContent className="p-3">
                <div className="text-xl font-bold leading-none">
                  {summary?.total_leads?.toLocaleString() || 0}
                </div>
                <p className="text-xs text-muted-foreground mt-0.5 leading-tight">
                  From {summary?.unique_sources || 0} sources
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-0">
                <CardTitle className="text-sm font-medium">Converted Leads</CardTitle>
                <TrendingUp
                  className="h-4 w-4"
                  aria-hidden="true"
                  style={{ color: CATEGORICAL.evergreen.solid }}
                />
              </CardHeader>
              <CardContent className="p-3">
                <div className="text-xl font-bold leading-none">
                  {summary?.total_converted?.toLocaleString() || 0}
                </div>
                <p className="text-xs text-muted-foreground mt-0.5 leading-tight">
                  Successfully converted
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-0">
                <CardTitle className="text-sm font-medium">Overall Conversion Rate</CardTitle>
                <Target
                  className="h-4 w-4"
                  aria-hidden="true"
                  style={{ color: CATEGORICAL.plum.solid }}
                />
              </CardHeader>
              <CardContent className="p-3">
                <div className="text-xl font-bold leading-none">
                  {formatPercent(summary?.overall_conversion_rate || 0)}
                </div>
                <p className="text-xs text-muted-foreground mt-0.5 leading-tight">
                  Across all sources
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-0">
                <CardTitle className="text-sm font-medium">Unique Sources</CardTitle>
                <DollarSign
                  className="h-4 w-4"
                  aria-hidden="true"
                  style={{ color: CATEGORICAL.clay.solid }}
                />
              </CardHeader>
              <CardContent className="p-3">
                <div className="text-xl font-bold leading-none">{summary?.unique_sources || 0}</div>
                <p className="text-xs text-muted-foreground mt-0.5 leading-tight">
                  Active lead sources
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Charts */}
          <div className="grid gap-4 md:grid-cols-2">
            {/* Distribution Pie Chart */}
            <Card>
              <CardHeader>
                <CardTitle>Lead Distribution by Source</CardTitle>
                <CardDescription>Total leads from each source</CardDescription>
              </CardHeader>
              <CardContent>
                {distributionChartData.length === 0 ? (
                  <div className="flex items-center justify-center h-[300px] text-muted-foreground">
                    No distribution data available
                  </div>
                ) : (
                  <div className="flex flex-col md:flex-row gap-4 items-stretch">
                    {/* Donut + centered total */}
                    <div className="relative flex-shrink-0 w-full md:w-[55%] h-[300px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={distributionChartData}
                            cx="50%"
                            cy="50%"
                            innerRadius={60}
                            outerRadius={95}
                            paddingAngle={1}
                            dataKey="value"
                            stroke="hsl(var(--background))"
                            strokeWidth={1}
                            isAnimationActive={false}
                          >
                            {distributionChartData.map((_entry: unknown, index: number) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip
                            content={<ChartTooltipContent />}
                            formatter={(value: number, _name: string, item: any) => [
                              `${value.toLocaleString()} leads (${
                                totalLeads > 0 ? ((value / totalLeads) * 100).toFixed(1) : '0.0'
                              }%)`,
                              item.payload.name,
                            ]}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                      <div
                        className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center"
                        aria-hidden="true"
                      >
                        <div className="text-2xl font-bold leading-none">
                          {totalLeads.toLocaleString()}
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">
                          {totalLeads === 1 ? 'lead' : 'leads'}
                        </div>
                      </div>
                    </div>
                    {/* Legend table */}
                    <div
                      className="flex-1 min-w-0 max-h-[300px] overflow-y-auto pr-1"
                      role="table"
                      aria-label="Lead sources with counts and percentages"
                    >
                      <div role="rowgroup">
                        {sortedDistribution.map((item: any) => {
                          const pct = totalLeads > 0 ? (item.value / totalLeads) * 100 : 0
                          return (
                            <div
                              key={item.name}
                              role="row"
                              className="flex items-center gap-2 py-1.5 border-b border-border last:border-b-0 text-sm"
                            >
                              <span
                                role="cell"
                                aria-hidden="true"
                                className="inline-block w-3 h-3 rounded-sm flex-shrink-0"
                                style={{
                                  backgroundColor: COLORS[item.colorIndex % COLORS.length],
                                }}
                              />
                              <span role="cell" className="flex-1 truncate" title={item.name}>
                                {item.name}
                              </span>
                              <span role="cell" className="tabular-nums text-muted-foreground">
                                {item.value.toLocaleString()}
                              </span>
                              <span
                                role="cell"
                                className="tabular-nums text-muted-foreground w-12 text-right"
                              >
                                {pct.toFixed(1)}%
                              </span>
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Top Performers Bar Chart */}
            <Card>
              <CardHeader>
                <CardTitle>Top Performing Sources</CardTitle>
                <CardDescription>Highest conversion rates (minimum 5 leads)</CardDescription>
              </CardHeader>
              <CardContent>
                {topPerformersChartData.length === 0 ? (
                  <div className="flex items-center justify-center h-[300px] text-muted-foreground">
                    No top performers data available
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={topPerformersChartData} margin={DEFAULT_CHART_MARGIN}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="source" tick={{ fontSize: 11 }} />
                      <YAxis tickFormatter={(v) => `${v}%`} tick={{ fontSize: 11 }} />
                      <Tooltip
                        content={<ChartTooltipContent />}
                        formatter={(value: number, name: string) => {
                          if (name === 'Conversion Rate') return `${value.toFixed(2)}%`
                          return value
                        }}
                      />
                      <Legend />
                      <Bar dataKey="conversionRate" fill={COLORS[0]} name="Conversion Rate" />
                    </BarChart>
                  </ResponsiveContainer>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Trends Line Chart */}
          <Card>
            <CardHeader>
              <CardTitle>Lead Volume Trends</CardTitle>
              <CardDescription>Monthly lead volume by source over time</CardDescription>
            </CardHeader>
            <CardContent>
              {trendsChartData.length === 0 ? (
                <div className="flex items-center justify-center h-[300px] text-muted-foreground">
                  No trends data available
                </div>
              ) : (
                <ResponsiveContainer width="100%" height={300}>
                  <LineChart data={trendsChartData} margin={DEFAULT_CHART_MARGIN}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                    <YAxis tickFormatter={formatChartCount} tick={{ fontSize: 11 }} />
                    <Tooltip content={<ChartTooltipContent />} />
                    <Legend />
                    {uniqueSources.map((source: string, index: number) => (
                      <Line
                        key={source}
                        type="monotone"
                        dataKey={source}
                        stroke={COLORS[index % COLORS.length]}
                        strokeWidth={2}
                        name={source}
                      />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              )}
            </CardContent>
          </Card>

          {/* Detailed Table */}
          <Card>
            <CardHeader>
              <CardTitle>Detailed Metrics by Source</CardTitle>
              <CardDescription>Complete breakdown of performance metrics</CardDescription>
            </CardHeader>
            <CardContent>
              {!data?.distribution || data.distribution.length === 0 ? (
                <div className="flex items-center justify-center py-8 text-muted-foreground">
                  No detailed metrics available
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table
                    className="w-full text-sm"
                    role="table"
                    aria-label="Lead source detailed metrics"
                  >
                    <thead>
                      <tr className="border-b">
                        <th className="text-left p-2 font-medium">Lead Source</th>
                        <th className="text-right p-2 font-medium">Total Leads</th>
                        <th className="text-right p-2 font-medium">Qualified</th>
                        <th className="text-right p-2 font-medium">Converted</th>
                        <th className="text-right p-2 font-medium">Conv. Rate</th>
                        <th className="text-right p-2 font-medium">Total Revenue</th>
                        <th className="text-right p-2 font-medium">Avg Deal Size</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.distribution.map((item: any, index: number) => (
                        <tr key={index} className="border-b hover:bg-muted/50">
                          <td className="p-2">{item.lead_source}</td>
                          <td className="text-right p-2">{item.total_leads}</td>
                          <td className="text-right p-2">{item.qualified_leads}</td>
                          <td className="text-right p-2">{item.converted_leads}</td>
                          <td className="text-right p-2">{formatPercent(item.conversion_rate)}</td>
                          <td className="text-right p-2">{formatCurrency(item.total_revenue)}</td>
                          <td className="text-right p-2">{formatCurrency(item.avg_deal_size)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  )
}
